import { useState, type FormEvent } from 'react';
import { useAppSelector } from '../../../Redux/hooks';
import { useTranslation } from '../../../i18n/useTranslation';
import { ApiError } from '../../../Models/ApiModel';
import { ADMIN_ACTIONS, type AdminAction, type AdminUserModel, type AuditEntryModel } from '../../../Models/AdminModel';
import { AdminService } from '../../../Services/AdminService';
import { EmptyState } from '../../SharedArea/EmptyState/EmptyState';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import { PremiumSubnav } from '../Premium/PremiumSubnav';
import './Admin.css';

const ACTION_KEYS = {
  grant: 'adminGrant',
  trial: 'adminTrial',
  cancel: 'adminCancel',
  revoke: 'adminRevoke',
  expire: 'adminExpire',
  renew: 'adminRenew',
  notes: 'adminNotes'
} as const;

/** The three that end someone's access, and so are asked about differently. */
const DESTRUCTIVE: AdminAction[] = ['cancel', 'revoke', 'expire'];

/**
 * Granting and revoking premium by hand, and reading the log of who did what.
 *
 * The screen is only reachable by an admin, but that is not what makes it
 * safe: every route it calls checks the caller against the server's admin list
 * and refuses anyone else. Hiding the link is a courtesy to everyone who is
 * not an admin, not the control.
 */
export function Admin() {
  const { tr } = useTranslation();
  const isAdmin = useAppSelector(state => state.session.subscription?.isAdmin ?? false);
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState<AdminUserModel[] | null>(null);
  const [audit, setAudit] = useState<{ userId: string; entries: AuditEntryModel[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  // One month unless the admin says otherwise. Emptied, a grant falls back to
  // the date beside it, and with neither it has no end.
  const [months, setMonths] = useState('1');

  if (!isAdmin) {
    return (
      <section className="admin">
        <EmptyState title={tr('adminOnly')} hint={tr('adminOnlyHint')} />
      </section>
    );
  }

  async function search(event: FormEvent) {
    event.preventDefault();
    if (!query.trim()) return;
    setBusy(true);
    setError(null);
    try {
      setUsers(await AdminService.searchUsers(query.trim()));
      setAudit(null);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
    } finally {
      setBusy(false);
    }
  }

  async function act(action: AdminAction, user: AdminUserModel) {
    setBusy(true);
    setError(null);
    try {
      await AdminService.act(action, {
        userId: user.id,
        ...(months ? { months: Number(months) } : {}),
        ...(expiresAt ? { expiresAt } : {}),
        ...(note.trim() ? { notes: note.trim() } : {})
      });
      // Re-read rather than patching the row: the server derives the state,
      // and guessing it here is how a screen starts lying about entitlement.
      setUsers(await AdminService.searchUsers(query.trim()));
      if (audit?.userId === user.id) await showAudit(user.id);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
    } finally {
      setBusy(false);
    }
  }

  async function showAudit(userId: string) {
    try {
      setAudit({ userId, entries: await AdminService.audit(userId) });
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
    }
  }

  return (
    <section className="admin">
      <PremiumSubnav />
      <h1 className="screen-title">{tr('navAdmin')}</h1>

      <form className="admin-search" onSubmit={search}>
        <input
          type="search"
          className="text-input"
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder={tr('adminSearchPlaceholder')}
          aria-label={tr('adminSearchPlaceholder')}
        />
        <button type="submit" className="primary-button" disabled={busy || !query.trim()}>
          {busy ? <Spinner inline /> : tr('search')}
        </button>
      </form>

      {/* Both apply to whichever action is pressed next. Kept beside the
          actions rather than inside a dialog per action, because an admin
          working through a list sets them once. */}
      <div className="admin-inputs">
        <label className="field">
          <span>{tr('adminMonths')}</span>
          <input
            className="text-input"
            inputMode="numeric"
            value={months}
            onChange={event => setMonths(event.target.value.replace(/\D/g, '').slice(0, 3))}
          />
        </label>
        <label className="field">
          <span>{tr('adminExpiresAt')}</span>
          <input className="text-input" type="date" value={expiresAt} onChange={event => setExpiresAt(event.target.value)} />
        </label>
        <label className="field">
          <span>{tr('adminNote')}</span>
          <input className="text-input" value={note} onChange={event => setNote(event.target.value)} />
        </label>
      </div>

      <p className="admin-hint">{tr('adminMonthsHint')}</p>

      {error && <p className="admin-error" role="alert">{tr('errorGeneric')}</p>}

      {users !== null && (users.length === 0
        ? <EmptyState title={tr('adminNoUsers')} hint={tr('adminNoUsersHint')} />
        : (
          <ul className="admin-users">
            {users.map(user => (
              <li key={user.id} className="admin-user">
                <header>
                  <strong>{user.displayName ?? user.email ?? user.id}</strong>
                  <bdi dir="ltr">{user.email}</bdi>
                  <span className={user.subscription.active ? 'admin-badge is-active' : 'admin-badge'}>
                    {user.subscription.status}
                  </span>
                </header>
                <p className="admin-user-meta">
                  <bdi dir="ltr">{user.id}</bdi>
                  {user.subscription.expiresAt && <> · <bdi>{user.subscription.expiresAt.slice(0, 10)}</bdi></>}
                  {user.subscription.grantedManually && <> · {tr('adminGrantedManually')}</>}
                </p>
                {user.subscription.notes && <p className="admin-user-notes">{user.subscription.notes}</p>}

                <div className="admin-actions">
                  {ADMIN_ACTIONS.map(action => (
                    <button
                      key={action}
                      type="button"
                      className={DESTRUCTIVE.includes(action) ? 'ghost-button is-destructive' : 'ghost-button'}
                      onClick={() => void act(action, user)}
                      disabled={busy}
                    >
                      {tr(ACTION_KEYS[action])}
                    </button>
                  ))}
                  <button type="button" className="ghost-button" onClick={() => void showAudit(user.id)} disabled={busy}>
                    {tr('adminAudit')}
                  </button>
                </div>

                {audit?.userId === user.id && (
                  <ol className="admin-audit">
                    {audit.entries.length === 0
                      ? <li className="is-empty">{tr('adminNoAudit')}</li>
                      : audit.entries.map(entry => (
                        <li key={entry.id}>
                          <bdi dir="ltr">{entry.createdAt.slice(0, 16).replace('T', ' ')}</bdi>
                          <strong>{entry.eventType}</strong>
                          <bdi dir="ltr">{entry.actorId}</bdi>
                        </li>
                      ))}
                  </ol>
                )}
              </li>
            ))}
          </ul>
        ))}
    </section>
  );
}
